/**
 * Run tasks with a ceiling on how many are in flight.
 *
 * History needs one Daily Status per eligible row, and a page is fifty rows.
 * Fifty parallel requests is a burst the API has no reason to absorb and the
 * browser will queue anyway; fifty sequential ones is a screen that fills in over
 * several seconds. §4.1 asks for "bounded concurrency with visible per-row
 * loading/error states", which is this plus a per-row state in the caller.
 *
 * **Every task settles.** A rejection becomes `{ok: false, error}` rather than
 * failing the batch, because one row whose Daily Status 404s must not blank the
 * other forty-nine — and a `Promise.all` here would do exactly that.
 */

export type Settled<T> = { ok: true; value: T } | { ok: false; error: unknown }

export const DEFAULT_CONCURRENCY = 4

/**
 * Results are returned in the order the tasks were given, never in completion
 * order, so a caller can zip them back onto its rows by index.
 */
export async function runBounded<T>(
  tasks: ReadonlyArray<() => Promise<T>>,
  limit: number = DEFAULT_CONCURRENCY,
): Promise<Settled<T>[]> {
  const results: Settled<T>[] = new Array(tasks.length)
  if (tasks.length === 0) return results

  let next = 0
  const workers = Array.from({ length: Math.max(1, Math.min(limit, tasks.length)) }, async () => {
    for (;;) {
      const index = next++
      if (index >= tasks.length) return
      try {
        results[index] = { ok: true, value: await tasks[index]() }
      } catch (error) {
        results[index] = { ok: false, error }
      }
    }
  })

  await Promise.all(workers)
  return results
}
